import * as React from "react"
import * as MDT from "models/mdt";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Box, Button, Typography } from "@mui/material";
import { FilterContext } from "models/core/filter";
import Select from "react-select";
import { SideBySideButton } from "components/common/SideBySideOptions";
import { exportMdtPatients, exportMdtExcludedPatients } from "models/mdt/worker";
import * as fileHelper from "helpers/fileHelper";
import Loader from "pages/shared/filterloader";

export function MdtFilters() {
	const { state, dispatch } = React.useContext(AppContext);

	const addFilter: MDT.AddFilterFunc = async (key: keyof typeof MDT.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await MDT.computeChartData(MDT.addFilter(state.page.dashboard as MDT.IMDTDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: MDT.RemoveFilterFunc = async (key: keyof typeof MDT.DashboardFilterKeys) => {

		const filteredDashboard = await MDT.computeChartData(MDT.removeFilter(state.page.dashboard as MDT.IMDTDashboard, key));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	if (state.page.loading)
		return <Loader />;

	const mdtData = state?.page?.dashboard?.chartData as MDT.IChartData;

	//HHS 
	//HHS Options
	const hhsOptions: any[] = [];
	mdtData?.hhses.map(p => {
		hhsOptions.push({ label: p.name, value: p.id });
	})

	//Filtered HHSes
	let hhsSelections: any[] = [];
	state.page.dashboard.filters?.HHS?.value?.map(p => {
		hhsSelections.push({ label: p, value: hhsOptions.find(a => a.label == p).value });
	})

	//Add code to be able to filter list of facilities to a chosen HHS
	let selectedHHSIds = [];
	if (hhsSelections.length > 0) {
		selectedHHSIds = hhsSelections.map(a =>
			a.value);
	}

	//Facility 
	//Facility Options
	const facilityOptions: any[] = [];
	if (hhsSelections.length > 0) {
		mdtData?.facilities.filter(a => selectedHHSIds.includes(a.hhsId)).map(p => {
			facilityOptions.push({ label: p.name, value: p.facilityId });
		})
	} else {
		mdtData?.facilities.map(p => {
			facilityOptions.push({ label: p.name, value: p.facilityId });
		});
	}

	//Filtered facilities
	let facilitySelections: any[] = [];
	state.page.dashboard.filters?.Facility?.value?.map(p => {
		facilitySelections.push({ label: p, value: facilityOptions.find(a => a.label == p).value });
	})

	//Add code to be able to filter list of Mdts to a chosen Facilities
	let selectedFacilityIds = [];
	if (facilitySelections.length > 0) {
		selectedFacilityIds = facilitySelections.map(a =>
			a.value);
	}

	//MDTs
	//Options
	const mdtOptions: any[] = [];
	if (facilitySelections.length > 0) {
		mdtData?.mdts.filter(a => selectedFacilityIds.includes(a.facilityId)).map(p => {
			mdtOptions.push({ label: p.nameAndFacility, value: p.mdtNameId });
		})
	} else {
		mdtData?.mdts.map(p => {
			mdtOptions.push({ label: p.nameAndFacility, value: p.mdtNameId });
		})
	}

	//Filtered MDTs
	let mdtSelections: any[] = [];
	state.page.dashboard.filters?.Mdt?.value?.map(p => {
		mdtSelections.push({ label: p, value: mdtOptions.find(a => a.label == p).value });
	})

	let { path } = mdtData.reviewsByPeriod;
	let parts = FilterContext.fromPath(path).parts;

	//Months
	//Options
	const monthOptions: any[] = [];
	mdtData?.month.map(p => {
		monthOptions.push({ label: p.name, value: p.id });
	})

	//Filtered MDT months
	let mdtMonthSelections: any[] = [];
	if (parts.length > 1) {
		parts[1].replace(`[`, ``).replace(`]`, ``).split('; ').map(p => {
			mdtMonthSelections.push({
				label: monthOptions.find(a => a.label.substring(0, 3) == p.replace(" ", ""))?.label,
				value: monthOptions.find(a => a.label.substring(0, 3) == p.replace(" ", ""))?.value
			});
		})
	}

	//Filtered diagnosis months
	let diagMonthSelections: any[] = [];
	state.page.dashboard.filters?.DiagMonth?.value?.map(p => {
		diagMonthSelections.push({ label: p, value: monthOptions.find(a => a.label == p).value });
	})

	//Years
	//MDT Year options
	const mdtYearOptions: any[] = [];
	mdtData?.year.map(p => {
		if (p.id >= 2010 && p.name != "Unknown") {
			mdtYearOptions.push({ label: p.name, value: p.id });
		}
	})

	//Filtered year selections
	let mdtYearSelections: any[] = [];
	if (parts.length > 0) {
		parts[0].replace(`[`, ``).replace(`]`, ``).split(';').map(p => {
			mdtYearSelections.push({
				label: p.replace(" ", ""),
				value: mdtData?.year.find(a => a.name == p.replace(" ", ""))?.id
			});
		})
	}

	//Diagnosis year options
	const diagYearOptions: any[] = [];
	mdtData?.year.map(p => {
		if (p.name != "Unknown") {
			diagYearOptions.push({ label: p.name, value: p.id });
		}
	})

	//Filtered diagnosis selections
	let diagYearSelections: any[] = [];
	state.page.dashboard.filters?.DiagYear?.value?.map(p => {
		diagYearSelections.push({ label: p, value: diagYearOptions.find(a => a.label == p).value });
	})

	//State for side by side button 
	const [viewBy, setViewBy] = React.useState(diagYearSelections.length > 0 ? "Diagnosis Year" : "MDT Year");

	return (
		<>
			<Box>
				<Typography>HHS</Typography>
				<Select
					isMulti={true}
					value={hhsSelections}
					options={hhsOptions}
					onChange={p => {
						const selected: any = p;
						let filterValues: string[] = [];
						selected.forEach(a => filterValues.push(a.label));

						if (filterValues.length == 0) {
							removeFilter(MDT.DashboardFilterKeys.HHS.key);
						} else {
							addFilter(MDT.DashboardFilterKeys.HHS.key, filterValues);
						}
					}}
					placeholder={"All"}
					closeMenuOnSelect={false}
					styles={{
						placeholder: (base) => ({
							...base,
							color: "black",
						}),
					}}
				/>
			</Box>


			<Box>
				<Typography>Facility</Typography>
				<Select
					isMulti={true}
					value={facilitySelections}
					options={facilityOptions}
					onChange={p => {
						const selected: any = p;
						let filterValues: string[] = [];
						selected.forEach(a => filterValues.push(a.label));

						if (filterValues.length == 0) {
							removeFilter(MDT.DashboardFilterKeys.Facility.key);
						} else {
							addFilter(MDT.DashboardFilterKeys.Facility.key, filterValues);
						}
					}}
					placeholder={"All"}
					closeMenuOnSelect={false}
					styles={{
						placeholder: (base) => ({
							...base,
							color: "black",
						}),
					}}
				/>
			</Box>


			<Box>
				<Typography>MDT</Typography>
				<Select
					isMulti={true}
					value={mdtSelections}
					options={mdtOptions}
					onChange={p => {
						const selected: any = p;
						let filterValues: string[] = [];
						selected.forEach(a => filterValues.push(a.label));

						if (filterValues.length == 0) {
							removeFilter(MDT.DashboardFilterKeys.Mdt.key);
						} else {
							addFilter(MDT.DashboardFilterKeys.Mdt.key, filterValues);
						}
					}}
					placeholder={"All"}
					closeMenuOnSelect={false}
					styles={{
						placeholder: (base) => ({
							...base,
							color: "black",
						}),
					}}
				/>
			</Box>

			<Box>
				<Typography>View By</Typography>
				<SideBySideButton
					filterItems={["MDT Year", "Diagnosis Year"]}
					currentState={viewBy}
					onChanged={p => {
						setViewBy(p)
					}}

				/>
			</Box>

			{(viewBy == "MDT Year" ?
				<>
					<Box>
						<Typography>MDT Year</Typography>
						<Select
							isMulti={true}
							value={mdtYearSelections}
							options={mdtYearOptions.reverse()}
							onChange={p => {
								const selected: any = p;
								let filterValues: string[] = [];
								selected.forEach(a => filterValues.push(a.label));
								filterValues.sort();

								if (mdtYearSelections.length > 0) {
									let context = path && FilterContext.fromPath(path) || new FilterContext();
									context.parts = [];

									if (filterValues.length == 0) {
										removeFilter(MDT.DashboardFilterKeys.Year.key);
									} else {
										addFilter(MDT.DashboardFilterKeys.Year.key, filterValues, context);
									}
								} else {
									const context = path && FilterContext.fromPath(path) || new FilterContext();
									addFilter(MDT.DashboardFilterKeys.Year.key, filterValues, context);
								}
							}}
							placeholder={"All"}
							closeMenuOnSelect={false}
							styles={{
								placeholder: (base) => ({
									...base,
									color: "black",
								}),
							}}
						/>
					</Box>

					{(mdtYearSelections && mdtYearSelections.length == 1 ?
						<Box>
							<Typography>MDT Month</Typography>
							<Select
								isMulti={true}
								value={mdtMonthSelections}
								options={monthOptions}
								onChange={p => {
									const selected: any = p;
									let filterValues: string[] = [];
									selected.forEach(a => filterValues.push(a.label.substring(0, 3)));

									if (parts.length > 1) {
										let context = path && FilterContext.fromPath(path) || new FilterContext();
										context.parts = [context.parts[0]];

										if (filterValues.length == 0) {
											let context = path && FilterContext.fromPath(path) || new FilterContext();
											context.parts = [context.parts[0]];
											addFilter(MDT.DashboardFilterKeys.Year.key, [], context);
										} else {
											addFilter(MDT.DashboardFilterKeys.Year.key, filterValues, context);
										}
									} else {
										const context = path && FilterContext.fromPath(path) || new FilterContext();
										addFilter(MDT.DashboardFilterKeys.Year.key, filterValues, context);
									}
								}}
								placeholder={"All"}
								closeMenuOnSelect={false}
								styles={{
									placeholder: (base) => ({
										...base,
										color: "black",
									}),
								}}
							/>
						</Box>

						: null)}
				</>
				:
				<>
					<Box>
						<Typography>Diagnosis Year</Typography>
						<Select
							isMulti={true}
							value={diagYearSelections}
							options={diagYearOptions.reverse()}
							onChange={p => {
								const selected: any = p;
								let filterValues: string[] = [];
								selected.forEach(a => filterValues.push(a.label));

								if (filterValues.length == 0) {
									removeFilter(MDT.DashboardFilterKeys.DiagYear.key);
								} else {
									addFilter(MDT.DashboardFilterKeys.DiagYear.key, filterValues);
								}
							}}
							placeholder={"All"}
							closeMenuOnSelect={false}
							styles={{
								placeholder: (base) => ({
									...base,
									color: "black",
								}),
							}}
						/>
					</Box>

					<Box>
						<Typography>Diagnosis Month</Typography>
						<Select
							isMulti={true}
							value={diagMonthSelections}
							options={monthOptions}
							onChange={p => {
								const selected: any = p;
								let filterValues: string[] = [];
								selected.forEach(a => filterValues.push(a.label));

								if (filterValues.length == 0) {
									removeFilter(MDT.DashboardFilterKeys.DiagMonth.key);
								} else {
									addFilter(MDT.DashboardFilterKeys.DiagMonth.key, filterValues);
								}
							}}
							placeholder={"All"}
							closeMenuOnSelect={false}
							styles={{
								placeholder: (base) => ({
									...base,
									color: "black",
								}),
							}}
						/>
					</Box>


				</>
			)}


			<Box>
				{(Object.keys(state.page.dashboard.filters).length !== 0 ?
					<div>
						<Button
							onClick={() =>
								exportMdtPatients(mdtData.filteredReferralIdCsv, false)
									.then(data => {
										if (data.errorMessage) {
											throw data.errorMessage;
										}
										return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportMdtPatientsElem")
									})
									.catch(err => {
										console.log(err)
									})
							}
						>Download Patient List
						</Button>
						<Button
							onClick={() =>
								exportMdtExcludedPatients(mdtData.excludedReferralIdCsv, false)
									.then(data => {
										if (data.errorMessage) {
											throw data.errorMessage;
										}
										return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportMdtPatientsElem")
									})
									.catch(err => {
										console.log(err)
									})
							}
						>Download Excluded Patients List
						</Button>
					</div>
					:
					<div>Make a filter selection in order to download patient list</div>)}
			</Box>

			<a id="exportMdtPatientsElem" style={{ display: "none" }}></a>
		</>
	)
}


